import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar, faUsers, faStar, faTrophy,
  faArrowUp, faArrowDown, faMedal, faSyncAlt,
  faExclamationTriangle, faDatabase
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { EvaluationContext } from '../layout/CunstomEvaluationEmployes';

// ============================================================
// CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('EvaluationDashboardCacheDB');
db.version(1).stores({
  analytics: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedAnalytics(key, allowExpired = false) {
  try {
    const entry = await db.analytics.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedAnalytics(key, data) {
  await db.analytics.put({ key, data, timestamp: Date.now() });
}
async function getStaleAnalytics(key) {
  return getCachedAnalytics(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES EN COURS
// ============================================================
const pendingFetches = new Map();

const API_EVAL = `${API_CONFIG2.apiUrl}employe/api_evaluation.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const EvaluationDashboard = ({ managerId = 0 }) => {
  const context = useContext(EvaluationContext) || {};
  const { refreshKey, setSource } = context;

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');

  const abortControllerRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ============================================================
  // FONCTION DE FETCH AVEC DÉDOUBLONNAGE, ANNULATION ET CACHE
  // ============================================================
  const fetchAnalytics = useCallback(async (forceRefresh = false, retryCount = 0) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = managerId ? `analytics_${managerId}` : 'analytics_global';
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setAnalytics(result);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
          setError(null);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        setError('Erreur : trop de tentatives. Veuillez réessayer plus tard.');
        showToast('Erreur : trop de tentatives.', 'danger');
      }
      return;
    }

    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    const fetchPromise = (async () => {
      try {
        if (!forceRefresh) {
          const cached = await getCachedAnalytics(cacheKey);
          if (cached && isMounted.current) {
            setAnalytics(cached);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            setError(null);
            return cached;
          }
        }

        const params = new URLSearchParams({ action: 'analytics' });
        if (managerId) params.append('evaluateur_id', managerId);
        const url = `${API_EVAL}?${params.toString()}`;
        const response = await fetch(url, { signal: controller.signal });

        if (response.status === 429) {
          const stale = await getStaleAnalytics(cacheKey);
          if (stale && isMounted.current) {
            setAnalytics(stale);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            setLoading(false);
            setRefreshing(false);
            setError('Utilisation du cache périmé (429)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchAnalytics(forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleAnalytics(cacheKey);
          if (stale && isMounted.current) {
            setAnalytics(stale);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            setLoading(false);
            setRefreshing(false);
            setError('Utilisation du cache périmé (erreur réseau)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          if (isMounted.current) {
            setAnalytics(data.data);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedAnalytics(cacheKey, data.data);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
            setError(null);
          }
          return data.data;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        const stale = await getStaleAnalytics(cacheKey);
        if (stale && isMounted.current) {
          setAnalytics(stale);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          setLoading(false);
          setRefreshing(false);
          setError('Utilisation du cache périmé (erreur)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          return stale;
        }
        if (isMounted.current) {
          setError(err.message || 'Erreur de chargement');
          setLoading(false);
          setRefreshing(false);
          showToast(err.message || 'Erreur de chargement', 'danger');
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [managerId, refreshing, setSource, showToast]);

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchAnalytics();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      pendingFetches.clear();
    };
  }, []);

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchAnalytics(true);
    }
  }, [refreshKey, fetchAnalytics]);

  // ============================================================
  // GESTIONNAIRE DE RAFRAÎCHISSEMENT LOCAL
  // ============================================================
  const handleRefresh = () => {
    if (!loading && !refreshing) {
      fetchAnalytics(true);
    }
  };

  // ============================================================
  // AFFICHAGE DES ÉTATS DE CHARGEMENT / ERREUR
  // ============================================================
  if (loading && !analytics) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des analyses...</p>
      </div>
    );
  }

  if (error && !analytics) {
    return (
      <div className="text-center py-5 text-danger">
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-3" />
        <p>{error}</p>
        <button className="monetra-btn primary" onClick={handleRefresh}>
          <FontAwesomeIcon icon={faSyncAlt} /> Réessayer
        </button>
      </div>
    );
  }

  if (!analytics) {
    return <p className="text-center text-muted">Aucune donnée disponible.</p>;
  }

  // ============================================================
  // UTILITAIRE POUR LES ÉTOILES
  // ============================================================
  const renderStars = (note) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FontAwesomeIcon
          key={i}
          icon={faStar}
          className={i <= note ? 'text-warning' : 'text-muted'}
          style={{ fontSize: 'clamp(0.7rem, 0.9vw, 0.8rem)', marginRight: 2 }}
        />
      );
    }
    return stars;
  };

  // ============================================================
  // RENDU AVEC STYLES RESPONSIFS OPTIMISÉS
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – EVALUATION DASHBOARD (mobile optimisé)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.35);
          --bg-primary: #0b1428;
          --bg-secondary: #0a0f1f;
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
        }

        .analytics-container {
          padding: 0;
        }

        .badge-source {
          font-size: clamp(0.5rem, 0.8vw, 0.65rem);
          padding: 3px 10px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
          display: inline-block;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .stat-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 20px;
          padding: clamp(0.8rem, 1.2vw, 1.2rem);
          box-shadow: 0 12px 40px rgba(0,0,0,0.5);
          transition: transform 0.2s, border-color 0.2s;
          height: 100%;
        }
        .stat-card:hover {
          transform: translateY(-4px);
          border-color: rgba(59, 130, 246, 0.25);
        }
        .stat-number {
          font-size: clamp(1.8rem, 4vw, 2.2rem);
          font-weight: 700;
          color: var(--accent-blue);
          line-height: 1;
        }
        .stat-label {
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .critere-bar {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: 8px;
          padding: 8px 12px;
          margin-bottom: 6px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
        }
        .critere-bar .label {
          width: 80px;
          font-size: clamp(0.7rem, 0.9vw, 0.8rem);
          flex-shrink: 0;
        }
        .progress-track {
          flex: 1;
          height: 6px;
          background: rgba(255,255,255,0.05);
          border-radius: 4px;
          margin: 0 10px;
          overflow: hidden;
          min-width: 40px;
        }
        .progress-fill {
          height: 100%;
          border-radius: 4px;
          background: linear-gradient(90deg, var(--accent-blue), var(--accent-dark-blue));
          transition: width 0.6s ease;
        }
        .critere-bar .value {
          font-weight: 600;
          min-width: 40px;
          text-align: right;
          font-size: clamp(0.7rem, 0.9vw, 0.8rem);
        }

        .top-employee {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: 12px;
          padding: 8px 12px;
          margin-bottom: 6px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
        }
        .top-employee:hover {
          background: rgba(255,255,255,0.04);
          border-color: rgba(59, 130, 246, 0.1);
        }
        .rank-badge {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-dark-blue));
          color: #fff;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 8px;
          flex-shrink: 0;
          font-size: clamp(0.7rem, 0.9vw, 0.8rem);
        }
        .top-employee .info {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 100px;
        }
        .top-employee .info .name {
          font-weight: 600;
          color: var(--text-primary);
          font-size: clamp(0.8rem, 1vw, 0.9rem);
        }
        .top-employee .info .sub {
          font-size: clamp(0.6rem, 0.7vw, 0.65rem);
          color: var(--text-secondary);
        }
        .top-employee .score {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .top-employee .score .number {
          font-weight: 700;
          color: var(--accent-blue);
          font-size: clamp(0.9rem, 1.1vw, 1rem);
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-warning { color: #f59e0b; }
        .text-success { color: var(--accent-blue); }
        .text-info { color: #60a5fa; }
        .text-white { color: var(--text-primary); }
        .text-danger { color: #ef4444; }

        .fw-bold { font-weight: 600; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-3 { margin-bottom: 0.75rem; }
        .mb-4 { margin-bottom: clamp(0.8rem, 1.5vw, 1.2rem); }
        .me-2 { margin-right: 0.5rem; }
        .mt-2 { margin-top: 0.5rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .text-center { text-align: center; }

        .row {
          display: flex;
          flex-wrap: wrap;
          margin: 0 -10px;
        }
        .col-md-4 {
          flex: 0 0 33.333%;
          max-width: 33.333%;
          padding: 0 10px;
        }
        .g-4 > * { margin-bottom: 1rem; }

        @media (max-width: 768px) {
          .col-md-4 { flex: 0 0 100%; max-width: 100%; }
          .critere-bar .label { width: 70px; }
          .progress-track { margin: 0 6px; }
          .top-employee .info { min-width: 60px; }
        }
        @media (max-width: 480px) {
          .critere-bar {
            flex-direction: column;
            align-items: stretch;
          }
          .critere-bar .label { width: 100%; }
          .progress-track { margin: 6px 0; }
          .critere-bar .value { text-align: left; }
          .top-employee {
            flex-direction: column;
            align-items: stretch;
          }
          .top-employee .score { justify-content: flex-start; }
          .top-employee .info { min-width: unset; }
          .stat-card { padding: 0.8rem; }
          .stat-number { font-size: 1.6rem; }
        }
        @media (max-width: 360px) {
          .stat-number { font-size: 1.4rem; }
          .badge-source { font-size: 0.55rem; padding: 2px 8px; }
        }

        .monetra-btn {
          padding: 5px 14px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.7rem, 0.85vw, 0.8rem);
          white-space: nowrap;
          min-height: 36px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.25);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }

        .toast-container {
          position: fixed;
          top: clamp(10px, 2vh, 20px);
          right: clamp(10px, 2vw, 20px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-width: 360px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 16px;
          padding: 10px 16px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
          font-size: clamp(0.8rem, 1.1vw, 0.9rem);
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-primary);
          opacity: 0.5;
          cursor: pointer;
          font-size: 1.1rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { opacity: 1; }
      `}</style>

      <div className="analytics-container">
        {/* En-tête avec badge source et bouton refresh */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
            {dataSource === 'cache' ? '📦 Cache' :
             dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
             dataSource === 'cache (partagé)' ? '🔄 Partage' :
             dataSource === 'live' ? '🔄 Live' :
             dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
          </span>
          <button className="monetra-btn primary" onClick={handleRefresh} disabled={loading || refreshing}>
            <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} />
            {refreshing ? 'Actualisation...' : 'Actualiser'}
          </button>
        </div>

        {/* Cartes KPI */}
        <div className="row g-4 mb-4">
          <div className="col-md-4">
            <div className="stat-card text-center">
              <FontAwesomeIcon icon={faChartBar} className="text-success mb-2" size="2x" />
              <div className="stat-number">{analytics.total_evaluations}</div>
              <div className="stat-label">Évaluations totales</div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="stat-card text-center">
              <FontAwesomeIcon icon={faStar} className="text-warning mb-2" size="2x" />
              <div className="stat-number">{analytics.note_moyenne_globale} / 5</div>
              <div className="stat-label">Note moyenne globale</div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="stat-card text-center">
              <FontAwesomeIcon icon={faUsers} className="text-info mb-2" size="2x" />
              <div className="stat-number">{analytics.top_employes.length}</div>
              <div className="stat-label">Employés dans le top</div>
            </div>
          </div>
        </div>

        {/* Notes par critère */}
        <div className="stat-card mb-4">
          <h5 className="text-white mb-3" style={{ fontSize: 'clamp(0.9rem, 1.3vw, 1.1rem)' }}>
            <FontAwesomeIcon icon={faStar} className="text-warning me-2" />
            Moyennes par critère
          </h5>
          {analytics.notes_par_critere.map((crit) => (
            <div key={crit.critere_id} className="critere-bar">
              <span className="label">{crit.libelle}</span>
              <div className="progress-track">
                <div
                  className="progress-fill"
                  style={{ width: `${(crit.moyenne / 5) * 100}%` }}
                ></div>
              </div>
              <span className="value">{crit.moyenne}/5</span>
            </div>
          ))}
        </div>

        {/* Top 5 employés */}
        <div className="stat-card">
          <h5 className="text-white mb-3" style={{ fontSize: 'clamp(0.9rem, 1.3vw, 1.1rem)' }}>
            <FontAwesomeIcon icon={faTrophy} className="text-warning me-2" />
            Top {analytics.top_employes.length} employés
          </h5>
          {analytics.top_employes.map((emp, index) => (
            <div key={emp.employe_id} className="top-employee">
              <div className="d-flex align-items-center" style={{ flexWrap: 'wrap', gap: '6px' }}>
                <div className="rank-badge">{index + 1}</div>
                <div className="info">
                  <span className="name">{emp.prenom} {emp.nom}</span>
                  <span className="sub">{emp.matricule} • {emp.nb_eval} éval.</span>
                </div>
              </div>
              <div className="score">
                {renderStars(Math.round(emp.moyenne))}
                <span className="number">{emp.moyenne}</span>
              </div>
            </div>
          ))}
          {analytics.top_employes.length === 0 && (
            <p className="text-muted">Aucun employé évalué pour le moment.</p>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faStar : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default EvaluationDashboard;